import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { postDatatoServer } from '../../utils/apiBase';
import ContextHelper from '../../ContextHooks/ContextHelper';
import toast from 'react-hot-toast';
import { FormField } from '../formFields/FormField';

const schemeOptions = [
  'AYUSHMAN BHARAT',
  'BPL',
  'APL',
  'Deemed Free',
  'PRIVATE',
  'Prison Patient',
  'VOTER ID',
  'ADHAR CARD',
  'OTHERS',
];

const validationRules = {
  'AYUSHMAN BHARAT': {
    label: 'Ayushman Bharat Code',
    validate: (code) => /^\d{14}$/.test(code),
    message: 'Ayushman Bharat Code must be 14 digits',
  },
  BPL: {
    label: 'BPL Card Number',
    validate: (code) => /^\d{9}$/.test(code),
    message: 'BPL Card Number must be 9 digits',
  },
  'VOTER ID': {
    label: 'Voter ID',
    validate: (code) => /^[A-Z]{3}\d{7}$/.test(code),
    message: 'Voter ID must be 3 uppercase letters followed by 7 digits',
  },
  'ADHAR CARD': {
    label: 'Aadhaar Number',
    validate: (code) => /^[2-9]{1}[0-9]{11}$/.test(code),
    message: 'Aadhaar Number must be a valid 12-digit number',
  },
};

const validateMobileNumber = (number) => {
  if (!number) return 'Mobile number is required';
  const digitsOnly = number.replace(/\D/g, '');
  if (digitsOnly.length < 10) {
    return `Please enter a valid 10-digit mobile number (${10 - digitsOnly.length} digits remaining)`;
  }
  if (digitsOnly.length > 10) {
    return 'Mobile number should not exceed 10 digits';
  }
  if (!/^[6-9]/.test(digitsOnly)) {
    return 'Mobile number should start with 6, 7, 8, or 9';
  }
  return null;
};

const isValidDate = (dateString) => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

const calculateAge = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const validateForm = (formData, setErrors) => {
  const newErrors = {};
  const requiredFields = [
    'title',
    'firstName',
    'patientType',
    'opdIpdNo',
    'email',
    'weight',
    'dob',
    'age',
    'mobile',
    'gender',
    'scheme',
  ];
  requiredFields.forEach((field) => {
    if (!formData[field]) {
      const formattedField =
        field
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .charAt(0)
          .toUpperCase() + field.replace(/([a-z])([A-Z])/g, '$1 $2').slice(1);
      newErrors[field] = `${formattedField} is required`;
    }
  });

  if (formData.scheme == 'BPL' && !formData.bplCategory) {
    newErrors.bplCategory = 'BPL category is required';
  }

  if (formData.scheme && validationRules[formData.scheme]) {
    const rule = validationRules[formData.scheme];
    if (!rule.validate(formData.schemeCode)) {
      newErrors.schemeCode = rule.message;
    }
  }

  if (formData.email && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(formData.email)) {
    newErrors.email = 'Please enter a valid email address';
  }

  const mobileError = validateMobileNumber(formData.mobile);
  if (mobileError) {
    newErrors.mobile = mobileError;
  }

  ['firstName', 'lastName'].forEach((field) => {
    if (formData[field] && (formData[field].length < 2 || formData[field].length > 50)) {
      newErrors[field] = `${field === 'firstName' ? 'First' : 'Last'} name must be between 2 and 50 characters`;
    }
  });

  if (formData.age) {
    const numAge = parseInt(formData.age, 10);
    if (isNaN(numAge) || numAge < 0 || numAge > 120) {
      newErrors.age = 'Age must be between 0 and 120';
    }
  }

  ['dob', 'letterDate'].forEach((field) => {
    if (formData[field] && !isValidDate(formData[field])) {
      newErrors[field] = 'Invalid date';
    }
  });

  if (formData.dob && formData.age) {
    const calculatedAge = calculateAge(formData.dob);
    if (Math.abs(calculatedAge - parseInt(formData.age, 10)) > 1) {
      newErrors.age = 'Age does not match date of birth';
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

const PatientRegistrationModal = ({ handleClose, fetchPatientData }) => {
  const { currentUser } = ContextHelper();
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    dob: '',
    age: '',
    aadharNumber: 'N/A',
    weight: '',
    mobile: '',
    gender: '',
    email: '',
    street: '',
    country: 'India',
    state: '',
    city: '',
    remarks: '',
    emergency: false,
    scheme: '',
    bplCategory: '',
    schemeCode: '',
    patientType: '',
    opdIpdNo: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'mobile') {
      const mobileError = validateMobileNumber(value);
      setErrors((prev) => ({
        ...prev,
        mobile: mobileError,
      }));
    }

    if (name === 'dob') {
      setFormData((prevData) => ({
        ...prevData,
        age: value ? calculateAge(value).toString() : '',
      }));
    }

    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (formData.emergency) {
      const updatedFormData = { ...formData };
      Object.keys(updatedFormData).forEach((key) => {
        if (!updatedFormData[key] && key !== 'firstName') {
          updatedFormData[key] = 'N/A';
        }
      });

      if (!updatedFormData.firstName) {
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(3, '0');

        const dateString = `emr${day}${month}${year}${hours}${minutes}${seconds}`;
        updatedFormData.firstName = dateString;
      }

      setLoading(true);
      try {
        postDatatoServer({
          end_point: `register-patient`,
          body: {
            ...updatedFormData,
            emergency: updatedFormData.emergency,
          },
          call_back: (res) => {
            if (res?.status === 'success' && res?.response) {
              handleClose();
              toast.success(res.response.message);
            } else {
              toast.error('Register Failed');
            }
          },
          props: { header: true, token: currentUser?.token },
        });
      } catch (error) {
        console.error('Error registering patient:', error);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (validateForm(formData, setErrors)) {
      setLoading(true);
      try {
        postDatatoServer({
          end_point: `register-patient`,
          body: {
            ...formData,
            emergency: formData.emergency,
          },
          call_back: (res) => {
            if (res?.status === 'success' && res?.response) {
              handleClose();

              toast.success(res.response.message);
            } else {
              toast.error('Register Failed');
            }
          },
          props: { header: true, token: currentUser?.token },
        });
      } catch (error) {
        console.error('Error registering patient:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container>
      <Modal show={true} onHide={handleClose} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Patient Registration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="emergency-checkbox"
                  label="Emergency"
                  name="emergency"
                  checked={formData.emergency}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      emergency: e.target.checked,
                    }))
                  }
                />
              </Col>
              <FormField
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={errors.title}
                options={['MR', 'MRS', 'MS']}
                required
              />
              <FormField
                label="First Name"
                name="firstName"
                value={formData?.firstName
                  ?.replace(/^\s+/, '')
                  .replace(/\s{2,}/g, ' ')
                  .trimStart()}
                onChange={handleChange}
                error={errors.firstName}
                required
              />
              <FormField
                label="Last Name"
                name="lastName"
                value={formData?.lastName
                  ?.replace(/^\s+/, '')
                  .replace(/\s{2,}/g, ' ')
                  .trimStart()}
                onChange={handleChange}
                error={errors.lastName}
              />
              <FormField
                label="Date of Birth"
                name="dob"
                value={formData.dob}
                type="date"
                onChange={handleChange}
                error={errors.dob}
                required
              />
              <FormField
                label="Age"
                name="age"
                value={formData.age}
                type="number"
                onChange={handleChange}
                error={errors.age}
                disabled={!!formData.dob}
                required
              />

              <FormField
                label="Weight"
                name="weight"
                value={formData?.weight?.replace(/\s+/g, '')}
                type="text"
                onChange={handleChange}
                error={errors.weight}
                required
              />
              <FormField
                label="Mobile"
                name="mobile"
                value={formData.mobile}
                type="tel"
                prefix="+91"
                onChange={handleChange}
                error={errors.mobile}
                required
              />
              <FormField
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                error={errors.gender}
                options={['MALE', 'FEMALE', 'OTHERS']}
                required
              />
              <FormField
                label="Scheme"
                name="scheme"
                error={errors.scheme}
                value={formData.scheme}
                onChange={handleChange}
                required
                options={schemeOptions}
              />

              {formData.scheme === 'BPL' && (
                <FormField
                  label="BPL Category"
                  name="bplCategory"
                  value={formData.bplCategory}
                  onChange={handleChange}
                  error={errors.bplCategory}
                  options={['Parents', 'Individuals']}
                  required
                />
              )}
              {validationRules[formData.scheme] && (
                <Form.Group as={Col} md={3} className="mb-3">
                  <Form.Label>{validationRules[formData.scheme].label}</Form.Label>

                  <Form.Control
                    type="text"
                    name="schemeCode"
                    value={formData.schemeCode}
                    onChange={handleChange}
                    required
                    placeholder={`Enter ${validationRules[formData.scheme].label}`}
                    isInvalid={!validationRules[formData.scheme].validate(formData.schemeCode)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationRules[formData.scheme].message}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <FormField
                label="Email"
                name="email"
                value={formData?.email?.replace(/\s+/g, '')}
                onChange={handleChange}
                error={errors.email}
                required
              />
              <FormField
                label="Street"
                name="street"
                value={formData?.street
                  ?.replace(/^\s+/, '')
                  .replace(/\s{2,}/g, ' ')
                  .trimStart()}
                onChange={handleChange}
                error={errors.street}
              />
              <FormField
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                error={errors.country}
                disabled
              />
              <FormField
                label="State"
                name="state"
                value={formData?.state
                  ?.replace(/^\s+/, '')
                  .replace(/\s{2,}/g, ' ')
                  .trimStart()}
                onChange={handleChange}
                error={errors.state}
              />
              <FormField
                label="City"
                name="city"
                value={formData?.city
                  ?.replace(/^\s+/, '')
                  .replace(/\s{2,}/g, ' ')
                  .trimStart()}
                onChange={handleChange}
                error={errors.city}
              />
              <FormField
                label="Patient Type"
                name="patientType"
                value={formData.patientType}
                onChange={handleChange}
                error={errors.patientType}
                required
                options={['OPD', 'IPD', 'MLC', 'Private', 'Prison Patient', 'Others']}
              />
              <FormField
                label="OPD/IPD No."
                name="opdIpdNo"
                value={formData?.opdIpdNo
                  ?.replace(/^\s+/, '')
                  .replace(/\s{2,}/g, ' ')
                  .trimStart()}
                onChange={handleChange}
                error={errors.opdIpdNo}
                required
              />

              <Form.Group as={Col} lg={3} md={6} sm={12} className="mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control as="textarea" name="remarks" value={formData.remarks} onChange={handleChange} />
              </Form.Group>
            </Row>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button type="submit" className="login-btn base-color-bg text-whit px-4" disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PatientRegistrationModal;
